import { APP_INITIALIZER, DEFAULT_CURRENCY_CODE, LOCALE_ID, NgModule, Optional, SkipSelf } from '@angular/core';
import { IconsModule } from 'app/core/modules/icons.module';
import localeEs from '@angular/common/locales/es';
import { DatePipe, registerLocaleData, TitleCasePipe } from '@angular/common';
import { environment } from '../../environments/environment';
import { AngularFireAuthGuardModule } from '@angular/fire/compat/auth-guard';
import { L10n, loadCldr } from '@syncfusion/ej2-base';

import * as numberingSystems from 'cldr-data/supplemental/numberingSystems.json';
import * as gregorian from 'cldr-data/main/es/ca-gregorian.json';
import * as numbers from 'cldr-data/main/es/numbers.json';
import * as timeZoneNames from 'cldr-data/main/es/timeZoneNames.json';
import {
  CalendarDateFormatter,
  CalendarEventTitleFormatter,
  CalendarModule,
  CalendarMomentDateFormatter,
  DateAdapter
} from 'angular-calendar';

import { adapterFactory } from 'angular-calendar/date-adapters/moment';
import { ScreenTrackingService, UserTrackingService } from '@angular/fire/analytics';
import { AngularFireStorageModule } from '@angular/fire/compat/storage';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireAuth, AngularFireAuthModule } from '@angular/fire/compat/auth';
import {
  AngularFireFunctionsModule,
  REGION,
  USE_EMULATOR as USE_EMULATOR_FUNCTIONS
} from '@angular/fire/compat/functions';
import { AngularFirestoreModule, USE_EMULATOR as USE_EMULATOR_FIRESTORE } from '@angular/fire/compat/firestore';
import { CustomDateFormatter } from './services/custom-date-formater.service';
import { CustomEventTitleFormatter } from '../modules/admin/schedule/schedule.component';
import { TranslocoCoreModule } from './modules/transloco.module';
import { AuthService } from './services/auth.service';
import moment from 'moment';
import { TitleService } from './services/title.service';
import { JsonDateInterceptor } from './interceptors/json-date-interceptor.service';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { ApiModule } from './providers/api.module';
import { Configuration } from './providers/configuration';

loadCldr(numberingSystems['default'], gregorian['default'], numbers['default'], timeZoneNames['default']);
L10n.load({
  'es': {
    'schedule': {
      'day': 'Día',
      'week': 'Semana',
      'workWeek': 'Semana de Trabajo',
      'month': 'Mes',
      'today': 'Hoy'
    },
    'calendar': {
      'today': 'Hoy'
    }
  }
});

const momentAdapterFactory = (): any => adapterFactory(moment);

registerLocaleData(localeEs);

export const servicesOnRun = (auth: AuthService): () => Promise<void> => (): Promise<void> => auth.load();

@NgModule({
  imports: [
    IconsModule,
    AngularFireModule.initializeApp(environment.firebase),
    AngularFireAuthModule,
    AngularFireAuthGuardModule,
    AngularFirestoreModule,
    AngularFireStorageModule,
    AngularFireFunctionsModule,
    TranslocoCoreModule,
    CalendarModule.forRoot(
      {
        provide: DateAdapter,
        useFactory: momentAdapterFactory
      },
      {
        dateFormatter: {
          provide: CalendarDateFormatter,
          useClass: CalendarMomentDateFormatter
        }
      }
    ),
    ApiModule.forRoot(() => new Configuration({
      basePath: environment.basePath,
      accessToken: (): string => localStorage.accessToken
    }))
  ],
  providers: [
    AuthService,
    {
      provide: LOCALE_ID,
      useValue: 'es-ES'
    },
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
    DatePipe,
    TitleCasePipe,
    CalendarDateFormatter,
    ScreenTrackingService,
    UserTrackingService,
    TitleService,
    { provide: REGION, useValue: 'europe-west1' },
    {
      provide: CalendarDateFormatter,
      useClass: CustomDateFormatter
    },
    {
      provide: CalendarEventTitleFormatter,
      useClass: CustomEventTitleFormatter
    },
    // {
    //   provide: HTTP_INTERCEPTORS,
    //   useClass: AuthInterceptor,
    //   multi: true
    // },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JsonDateInterceptor,
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: servicesOnRun,
      multi: true,
      deps: [ AuthService, AngularFireAuth ]
    },
    { provide: USE_EMULATOR_FIRESTORE, useValue: environment.useEmulators ? [ 'localhost', 8080 ] : null },
    { provide: USE_EMULATOR_FUNCTIONS, useValue: environment.useEmulators ? [ 'localhost', 5001 ] : null },
    // {
    //   provide: ErrorHandler,
    //   useValue: Sentry.createErrorHandler(),
    // },
    // {
    //   provide: Sentry.TraceService,
    //   deps: [Router],
    // },
    // {
    //   provide: APP_INITIALIZER,
    //   useFactory: () => () => {},
    //   deps: [Sentry.TraceService],
    //   multi: true,
    // }
  ]
})
export class CoreModule {
  /**
   * Constructor
   */
  constructor(
    @Optional() @SkipSelf() parentModule?: CoreModule
  ) {
    // Do not allow multiple injections
    if (parentModule) {
      throw new Error('CoreModule has already been loaded. Import this module in the AppModule only.');
    }
  }
}
